/*ReactJS*/
import React, { useContext, useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';

/*Service*/
import { TableSettings } from '../../../proto/admpb/tablesettings_grpc_web_pb';
import { ListSecurityMasterRequest } from '../../../proto/assetpb/securitymaster_grpc_web_pb';
import { Pagination } from '../../../proto/utilspb/pagination_pb';

/*Toast Notification*/
import {
    notifyError,
    notifyInfo,
    notifySuccess,
} from '../../../components/Notification/Notification';

/*Material UI Table Components*/
import {
    Box,
    Button,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
} from '@material-ui/core';

/*Material UI Table*/
import MUIDataTable from 'mui-datatables';

import {
    Add as AddIcon,
    Create as EditIcon,
    Save as SaveIcon,
} from '@material-ui/icons';

/*Moment JS*/
import moment from 'moment-timezone';

/*Styles*/
import { MuiThemeProvider } from '@material-ui/core/styles';
import tableTheme from '../../../components/Table/TableStyle';
import useStyles from '../../../styles';

/*Custom components*/
import SelectSymbol from '../../../components/AutoComplete/SelectSymbol';
import SearchButton from '../../../components/Button/Search';
import { ASSET_TYPE_OPTIONS } from './constants';
import SecurityMasterFormModal from './SecurityMasterFormModal';

import { ServiceContext } from 'context/ServiceContext';
import { useUserState } from 'context/UserContext';

const fileName =
    'SecurityMaster_' + moment().format('MMMM Do YYYY, h:mm:ss a') + '.csv';

function formatDateForDisplay(dateObj) {
    if (!dateObj || !dateObj.year) {
        return '--';
    }
    return moment({
        year: dateObj.year,
        month: dateObj.month - 1,
        day: dateObj.day,
    }).format('MM/DD/YYYY');
}

function formatDateForInput(dateObj) {
    if (!dateObj || !dateObj.year) {
        return '';
    }
    const month = String(dateObj.month).padStart(2, '0');
    const day = String(dateObj.day).padStart(2, '0');
    return `${dateObj.year}-${month}-${day}`;
}

function mapSecurityMasterToRow(data) {
    return {
        securityId: data.securityId,
        originalCusip: data.originalCusip,
        symbol: data.symbol,
        cusip: data.cusip,
        symbolDescription: data.symbolDescription,
        marginable: data.marginable,
        securityType: data.securityType,
        secSubType: data.secSubType,
        assetType: data.assetType,
        whenIssued: data.whenIssued,
        leverageFactor: data.leverageFactor,
        startDate: formatDateForDisplay(data.startDate),
        endDate: formatDateForDisplay(data.endDate),
        startDateRaw: formatDateForInput(data.startDate),
        endDateRaw: formatDateForInput(data.endDate),
        createdAt: data.createdAt
            ? moment(new Date(data.createdAt.seconds * 1000)).format(
                'MM/DD/YYYY hh:mm'
            )
            : '--',
    };
}

const COLUMN_COUNT = 14;

function defaultViewColumns(columnCount) {
    return Array(columnCount).fill('true');
}

function normalizeViewColumns(viewColumns, columnCount) {
    if (!viewColumns || viewColumns.length !== columnCount) {
        return defaultViewColumns(columnCount);
    }
    return viewColumns.map((value) =>
        value === 'true' || value === true ? 'true' : 'false'
    );
}

function applyViewColumns(tableState, viewColumns) {
    const normalized = normalizeViewColumns(
        viewColumns,
        tableState.columns.length
    );
    for (let i = 0; i < tableState.columns.length; i++) {
        if (tableState.columns[i]) {
            tableState.columns[i].display = normalized[i];
        }
    }
    return normalized;
}

function isColumnVisible(display) {
    return display !== false && display !== 'false';
}

export default function SecurityMasterTable({ params }) {
    var { googleAccount } = useUserState();

    const classes = useStyles();
    const {
        securityMasterServiceClient: securityMaster,
        tableSettingsServiceClient: tableSettings,
    } = useContext(ServiceContext);

    const [symbolValue, setSymbolValue] = React.useState('');
    const [cusipValue, setCusipValue] = React.useState('');
    const [assetTypeValue, setAssetTypeValue] = React.useState('E');
    const [currentPath, setCurrentPath] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [showFilter, setShowFilter] = React.useState(true);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalRows, setTotalRows] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [editForm, setEditForm] = useState(null);
    const latestRequestId = useRef(0);

    useEffect(() => {
        const query = new URLSearchParams(params.location.search);
        const path = params.truepath;
        const data = {
            symbol: query.get('symbol'),
            cusip: query.get('cusip'),
        };

        if (data.symbol) {
            setSymbolValue(data.symbol);
        }
        if (data.cusip) {
            setCusipValue(data.cusip);
        }

        setCurrentPath(path);
    }, [params]);

    const search = (requestedPage = 0, requestedRowsPerPage = rowsPerPage) => {
        const pathWithParams =
            currentPath +
            '?search=true' +
            (symbolValue ? '&symbol=' + symbolValue : '') +
            (cusipValue ? '&cusip=' + cusipValue : '');
        window.history.pushState({}, null, pathWithParams);

        setLoading(true);

        let list = new ListSecurityMasterRequest();
        list.setSymbol(symbolValue);
        list.setCusip(cusipValue);
        if (assetTypeValue !== '') {
            list.setAssetTypesList([assetTypeValue]);
        }
        const pagination = new Pagination();
        pagination.setPageNo(requestedPage);
        pagination.setPageSize(requestedRowsPerPage);
        list.setPagination(pagination);

        latestRequestId.current += 1;
        const requestId = latestRequestId.current;

        securityMaster.listSecurityMaster(list, {}, (err, res) => {
            // A newer search is already in flight; its response is the one that
            // must win, so drop this one instead of rolling the table back.
            if (requestId !== latestRequestId.current) {
                return;
            }

            if (err) {
                console.error(err);
                notifyError(err.message);
                setLoading(false);
                return;
            }
            const response = res.toObject();
            const mappedRows = response.securityMastersList.map((data) =>
                mapSecurityMasterToRow(data)
            );

            setRows(mappedRows);
            setPage(requestedPage);
            setRowsPerPage(requestedRowsPerPage);
            setTotalRows(response.summary.totalRows);
            notifyInfo(response.summary.totalRows + ' search results.');
            setLoading(false);
        });
    };

    const handleOpenAdd = () => {
        setModalMode('add');
        setEditForm(null);
        setModalOpen(true);
    };

    const handleOpenEdit = (rowData) => {
        setModalMode('edit');
        setEditForm({
            securityId: rowData.securityId,
            symbol: rowData.symbol,
            cusip: rowData.cusip,
            originalCusip: rowData.originalCusip,
            startDate: rowData.startDateRaw,
            endDate: rowData.endDateRaw,
        });
        setModalOpen(true);
    };

    const handleSaved = () => {
        search();
    };

    const updateTableSettings = () => {
        let req = new TableSettings();
        req.setGoogleId(googleAccount.googleId);
        req.setPageName('Security Master');
        req.setViewColumns(tableSetting.viewColumns.toString());

        tableSettings.updateTableSettings(req, {}, (err, res) => {
            if (err) {
                console.error(err);
                return;
            }
            notifySuccess('Table has been saved.');
        });
    };

    const [tableSetting, setTableSetting] = useState({
        load: true,
        viewColumns: [],
    });

    const columns = [
        {
            name: '',
            options: {
                display: 'true',
                draggable: false,
                resizable: false,
                print: false,
                searchable: false,
                filter: false,
                sort: false,
                empty: true,
                viewColumns: false,
                customBodyRenderLite: (dataIndex) => {
                    if (!rows[dataIndex]) {
                        return null;
                    }
                    return (
                        <IconButton
                            aria-label="edit"
                            onClick={() => handleOpenEdit(rows[dataIndex])}
                        >
                            <EditIcon />
                        </IconButton>
                    );
                },
            },
        },
        {
            name: 'assetType',
            label: 'Asset Type',
        },
        {
            name: 'createdAt',
            label: 'Created At',
        },
        {
            name: 'cusip',
            label: 'Cusip',
        },
        {
            name: 'leverageFactor',
            label: 'Leverage Factor',
        },
        {
            name: 'marginable',
            label: 'Marginable',
        },
        {
            name: 'originalCusip',
            label: 'Original Cusip',
        },
        {
            name: 'secSubType',
            label: 'Sec Sub Type',
        },
        {
            name: 'securityType',
            label: 'Security Type',
        },
        {
            name: 'symbol',
            label: 'Symbol',
        },
        {
            name: 'symbolDescription',
            label: 'Symbol Description',
        },
        {
            name: 'whenIssued',
            label: 'When Issued',
        },
        {
            name: 'startDate',
            label: 'Start Date',
        },
        {
            name: 'endDate',
            label: 'End Date',
        },
    ];

    const options = {
        filterType: 'select',
        download: true,
        filter: true,
        search: false,
        print: false,
        sort: false,
        viewColumns: true,
        columnOrder: [0, 6, 9, 3, 10, 5, 8, 7, 1, 11, 4, 12, 13, 2],
        selectableRowsHeader: false,
        selectableRows: 'none',
        serverSide: true,
        count: totalRows,
        page,
        rowsPerPage,
        customToolbar: function () {
            return (
                <React.Fragment>
                    <Tooltip title="Add New" arrow>
                        <IconButton onClick={() => handleOpenAdd()}>
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Save Table" arrow>
                        <IconButton onClick={() => updateTableSettings()}>
                            <SaveIcon />
                        </IconButton>
                    </Tooltip>
                </React.Fragment>
            );
        },
        onDownload: () => {
            document.getElementById('csvDL').click();

            return false;
        },
        onTableChange: (action, tableState) => {
            if (action === 'changePage' || action === 'changeRowsPerPage') {
                search(tableState.page, tableState.rowsPerPage);
            }

            if (tableSetting.viewColumns.length === 0) {
                let req = new TableSettings();
                req.setGoogleId(googleAccount.googleId);
                req.setPageName('Security Master');

                tableSettings.readTableSettings(req, {}, (err, res) => {
                    if (err) {
                        const defaultColumns = defaultViewColumns(tableState.columns.length);
                        applyViewColumns(tableState, defaultColumns);

                        let req = new TableSettings();
                        req.setGoogleId(googleAccount.profileObj.googleId);
                        req.setUserName(googleAccount.profileObj.name);
                        req.setPageName('Security Master');
                        req.setViewColumns(defaultColumns.join(','));

                        tableSettings.createTableSettings(req, {}, (err, res) => {
                            if (err) {
                                console.error(err);
                                return;
                            }
                        });

                        setTableSetting({
                            load: true,
                            viewColumns: defaultColumns,
                        });

                        return;
                    } else {
                        let viewColumnState = res.toObject().tableSetting.viewColumns;
                        let viewColumns = viewColumnState.split(',');
                        const normalized = applyViewColumns(tableState, viewColumns);

                        setTableSetting({
                            load: true,
                            viewColumns: normalized,
                        });
                    }
                });
            } else {
                if (tableSetting.load) {
                    applyViewColumns(tableState, tableSetting.viewColumns);
                    tableSetting.load = false;
                }
            }

            let viewColumnState = [];

            for (let i = 0; i < tableState.columns.length; i++) {
                if (tableState.columns[i]) {
                    viewColumnState.push(
                        isColumnVisible(tableState.columns[i].display) ? 'true' : 'false'
                    );
                }
            }

            tableSetting.viewColumns = viewColumnState;
        },
    };

    return (
        <div className={classes.root}>
            <Box component="div" mt={5}>
                {showFilter ? (
                    <div>
                        <div className={classes.grdRow}>
                            <div className={classes.grdCell1} style={{ marginRight: 30 }}>
                                <SelectSymbol
                                    freeSolo={true}
                                    name="symbol"
                                    label="Symbol"
                                    type="symbol"
                                    value={symbolValue}
                                    onChange={(e) => {
                                        setSymbolValue(e.currentTarget.value);
                                    }}
                                />
                            </div>
                            <div className={classes.grdCell1} style={{ marginRight: 30 }}>
                                <TextField
                                    fullWidth
                                    label="Cusip"
                                    type="text"
                                    value={cusipValue}
                                    onChange={(e) => setCusipValue(e.target.value)}
                                    className={classes.textField}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </div>
                            <div className={classes.grdCell1}>
                                <InputLabel shrink>Asset Type</InputLabel>
                                <Select
                                    labelId="Asset Type"
                                    displayEmpty
                                    fullWidth
                                    value={assetTypeValue}
                                    onChange={(e) => setAssetTypeValue(e.target.value)}
                                >
                                    {ASSET_TYPE_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                            <div className={classes.grdCell1}></div>
                        </div>
                    </div>
                ) : null}
                <div className={classes.actionContainer}>
                    <div className={classes.grdCellNone} style={{ marginRight: 10 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenAdd()}
                        >
                            Add New
                        </Button>
                    </div>
                    <div className={classes.grdCellNone}>
                        <SearchButton
                            onClick={() => search()}
                            loading={loading}
                            showfilter={(status) => setShowFilter(status)}
                        />
                    </div>
                </div>
            </Box>
            <Box component="div" mt={2}>
                {rows && (
                    <CSVLink id="csvDL" data={rows} filename={fileName} target="_blank" />
                )}
                <MuiThemeProvider theme={tableTheme()}>
                    <MUIDataTable
                        key={`security-master-${COLUMN_COUNT}`}
                        title={'Security Master'}
                        data={rows}
                        columns={columns}
                        options={options}
                    />
                </MuiThemeProvider>
            </Box>
            <SecurityMasterFormModal
                open={modalOpen}
                mode={modalMode}
                initialForm={editForm}
                securityMasterClient={securityMaster}
                onClose={() => setModalOpen(false)}
                onSaved={handleSaved}
            />
        </div>
    );
}
